import { Info } from '@mui/icons-material';
import { Box, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { PostProcessingMode } from '~/engine/util/algorithms/sdf/SDFPostProcessing';

// ── 2D / 3D connectivity mode toggle (shared by the panels) ─────────

/**
 * The explanation hangs off an adjacent Info icon rather than wrapping
 * the group in a Tooltip. A tooltip on the control itself would open over
 * the very buttons the user is reaching for, hiding what the click was
 * about to change — the opposite of what an explanation is for. The
 * icon is the panel-wide idiom (see `Inputs/InlineToggle`), and hovering
 * it leaves the toggle visible.
 *
 * `labelId` / `tooltipId` exist because the two buttons do not mean the
 * same thing everywhere: for the SDF tabs they pick a connectivity, for
 * the eraser's rectangle they pick only how deep the drawn box is
 * extruded. The control is shared; the sentence explaining it cannot be.
 */
export default function ModeToggle({
    mode,
    onChange,
    disabled,
    labelId = 'postProcessing.mode',
    tooltipId = 'postProcessing.mode.tooltip',
}: {
    mode: PostProcessingMode;
    onChange: (mode: PostProcessingMode) => void;
    disabled: boolean;
    labelId?: string;
    tooltipId?: string;
}) {
    const intl = useIntl();
    const handleChange = (
        _event: React.MouseEvent<HTMLElement>,
        value: PostProcessingMode | null
    ) => {
        // ToggleButtonGroup emits null when the active button is re-clicked;
        // ignore it so a mode is always selected.
        if (value !== null) {
            onChange(value);
        }
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ToggleButtonGroup
                value={mode}
                exclusive
                onChange={handleChange}
                size="small"
                disabled={disabled}
                aria-label={intl.formatMessage({ id: labelId })}
            >
                <ToggleButton value="2D">
                    <FormattedMessage id="postProcessing.mode.2d" />
                </ToggleButton>
                <ToggleButton value="3D">
                    <FormattedMessage id="postProcessing.mode.3d" />
                </ToggleButton>
            </ToggleButtonGroup>
            <Tooltip
                title={<FormattedMessage id={tooltipId} />}
                placement="right"
            >
                <Info
                    tabIndex={0}
                    role="button"
                    aria-label={intl.formatMessage({ id: tooltipId })}
                    sx={{ fontSize: 12, opacity: 0.6 }}
                />
            </Tooltip>
        </Box>
    );
}
